import { termsCoEfficientTable } from "../../../../../constants";
import { normalizeCalculatedFleet } from "../../dataMapping";
import { safeArray, safeJsonParse, safeNumber, safeString } from "./validations";

/**
 * Maps fleet list with API calculated cost results
 */
export const fleetWithCalculatorDataMapping = (fleetList = [], response = {}) => {
  const safeFleetList = safeArray(fleetList);
  if (!response) {
    return safeFleetList.map((item) => normalizeCalculatedFleet({ ...item, selectedForSave: false }));
  }

  return safeFleetList.map((fleet) => {
    const matchedFleet = response[fleet.vesselUniqueId] || {};
    return normalizeCalculatedFleet({
      ...fleet,
      ...matchedFleet,
      vesselId: fleet.vesselId,
      selectedForSave: false,
    });
  });
};

/**
 * Safe version of addPortDistanceToOperations that prevents undefined crashes
 */
export const addPortDistanceToOperations = (
  res = {},
  portOperations = [],
  realData = {},
  addGeometry = false,
  reset = false
) => {
  const safeOperations = safeArray(portOperations).map((op) => ({ ...op }));
  const distanceData = (res && res.data) || res || {};

  for (let i = 0; i < safeOperations.length - 1; i++) {
    const currentPortObj = safeOperations[i] || {};
    const nextPortObj = safeOperations[i + 1] || {};

    const currentPortName = safeString(currentPortObj.port);
    const nextPortName = safeString(nextPortObj.port);

    if (!currentPortName || !nextPortName) continue;

    const combPortName = `${currentPortName}_${nextPortName}`;

    if (distanceData[combPortName] && nextPortObj.operation) {
      const legData = distanceData[combPortName];
      const oldCargoDetails = nextPortObj.cargoDetails || {};
      const isSameLeg = !oldCargoDetails.portCombination || oldCargoDetails.portCombination === combPortName;

      const apiNormal = realData?.[combPortName]?.distNormal !== undefined
        ? realData[combPortName].distNormal
        : legData?.distNormal;

      const apiEca = realData?.[combPortName]?.distEca !== undefined
        ? realData[combPortName].distEca
        : legData?.distEca;

      const systemDistNormal = apiNormal !== undefined
        ? safeNumber(apiNormal)
        : (isSameLeg && oldCargoDetails.distNormalSystem !== undefined ? safeNumber(oldCargoDetails.distNormalSystem) : 0);

      const systemDistEca = apiEca !== undefined
        ? safeNumber(apiEca)
        : (isSameLeg && oldCargoDetails.distEcaSystem !== undefined ? safeNumber(oldCargoDetails.distEcaSystem) : 0);

      // User-set distances are always non-zero — new port legs default to distNormal=0.
      // We treat distNormal=0 as "not explicitly set by user" so new ports always
      // initialize from system values. This works regardless of portCombination being
      // saved in the DB or not.
      const hasUserNormal = isSameLeg &&
        oldCargoDetails.distNormal !== undefined &&
        oldCargoDetails.distNormal !== null &&
        oldCargoDetails.distNormal !== "" &&
        Number(oldCargoDetails.distNormal) !== 0;

      const hasUserEca = isSameLeg &&
        oldCargoDetails.distEca !== undefined &&
        oldCargoDetails.distEca !== null &&
        oldCargoDetails.distEca !== "" &&
        Number(oldCargoDetails.distEca) !== 0;

      // A genuine user override means distNormal diverges BOTH from the new system
      // distance AND from the previously-stored system value (distNormalSystem).
      // If distNormal === distNormalSystem, the user never changed it — the system
      // set both fields to the same value. This distinguishes "user edited 150→120"
      // from "system set 150 last time, now system says 200 after reorder".
      const isNormalUserOverridden = hasUserNormal &&
        Number(oldCargoDetails.distNormal) !== Number(systemDistNormal) &&
        (oldCargoDetails.distNormalSystem == null
          ? true // legacy data without distNormalSystem: preserve old behaviour
          : Number(oldCargoDetails.distNormal) !== Number(oldCargoDetails.distNormalSystem));

      const isEcaUserOverridden = hasUserEca &&
        Number(oldCargoDetails.distEca) !== Number(systemDistEca) &&
        (oldCargoDetails.distEcaSystem == null
          ? true // legacy data without distEcaSystem: preserve old behaviour
          : Number(oldCargoDetails.distEca) !== Number(oldCargoDetails.distEcaSystem));

      const distNormal = (!reset && isNormalUserOverridden)
        ? safeNumber(oldCargoDetails.distNormal, systemDistNormal)
        : systemDistNormal;

      const distEca = (!reset && isEcaUserOverridden)
        ? safeNumber(oldCargoDetails.distEca, systemDistEca)
        : systemDistEca;

      let geoJSON = null;
      if (addGeometry && legData.geometry) {
        try {
          geoJSON = typeof legData.geometry === "string"
            ? JSON.parse(legData.geometry)
            : legData.geometry;
        } catch (err) {
          console.error("Failed to parse geometry for leg:", combPortName, err);
        }
      }

      safeOperations[i + 1] = {
        ...nextPortObj,
        cargoDetails: {
          ...oldCargoDetails,
          distNormal,
          distEca,
          distNormalSystem: systemDistNormal,
          distEcaSystem: systemDistEca,
          portCombination: combPortName,
        },
        geoJSON: addGeometry ? geoJSON : (nextPortObj.geoJSON || null),
      };
    }
  }

  return safeOperations;
};

/**
 * Builds distance query parameters from port operations and fleet list
 */
export const convertPortOperationToDistanceString = (calculatorData = {}) => {
  const portOperations = safeArray(calculatorData.portOperation);
  const fleetList = safeArray(calculatorData.fleetList);

  const routes = [];

  for (let i = 0; i < fleetList.length; i++) {
    const fleetPort = safeString(fleetList[i]?.port);
    const secondPortName = safeString(portOperations[1]?.port);
    if (fleetPort && secondPortName) {
      const portEntry = { name: fleetPort, lat: fleetList[i]?.lat || 0, lng: fleetList[i]?.lng || 0 };
      const secondPort = { name: secondPortName, lat: portOperations[1]?.lat || 0, lng: portOperations[1]?.lng || 0 };
      routes.push({ from: portEntry, to: secondPort });
    }
  }

  for (let i = 1; i < portOperations.length - 1; i++) {
    const currentPort = safeString(portOperations[i]?.port);
    const nextPort = safeString(portOperations[i + 1]?.port);
    if (currentPort && nextPort) {
      const portEntry = { name: currentPort, lat: portOperations[i]?.lat || 0, lng: portOperations[i]?.lng || 0 };
      const portEntry2 = { name: nextPort, lat: portOperations[i + 1]?.lat || 0, lng: portOperations[i + 1]?.lng || 0 };
      routes.push({ from: portEntry, to: portEntry2 });
    }
  }

  return { routeList: routes, getPortDistanceString: routes };
};

/**
 * Map terms coefficient safely
 */
export const getTermFactor = (termLabel) => {
  if (!termLabel) return 0;
  const match = termsCoEfficientTable.find((item) => item.label === termLabel);
  return match ? match.value : 0;
};

/**
 * Maps leg 1 distance to selected fleet
 */
export const addPortDistanceToSelectedFleet = (
  portDistance = [],
  selectedFleet = {}
) => {
  const { cargoDetails = {} } = (portDistance && portDistance[1]) || {};
  const { distNormal = 0, distEca = 0 } = cargoDetails;

  return {
    ...selectedFleet,
    distanceECA: distEca,
    distanceNormal: distNormal,
  };
};

/**
 * Validates calculation data and returns list of error/alert objects.
 * Exact copy of old dataMapping.js calculationValidation (L599-698).
 */
export const calculationValidation = (portOperation = [], selectedVessels = {}) => {
  const errors = [];
  const vessel =
    (selectedVessels &&
      selectedVessels.vessel &&
      JSON.parse(selectedVessels.vessel)) ||
    {};

  if (
    selectedVessels.stowage &&
    !isNaN(selectedVessels.stowage) &&
    Number(selectedVessels.stowage) > 98
  ) {
    errors.push({ message: "Stowage exceeds 98%", type: "error" });
  }

  for (let i = 0; i < portOperation.length; i++) {
    const singlePort = portOperation[i];
    const isLoadingDischarging =
      singlePort.operation === "Loading" ||
      singlePort.operation === "Discharging";

    if (isLoadingDischarging && isNaN(Number(singlePort.cargoTx))) {
      errors.push({
        message: `${singlePort.operation} Cargo Tx is Invalid`,
        type: "alert",
      });
    }
    if (
      isLoadingDischarging &&
      Number(singlePort.cargoTx) < Number(singlePort.min)
    ) {
      errors.push({
        message: `${singlePort.operation} Cargo less than minimum contractual quantity.`,
        type: "alert",
      });
    }
    if (Number(singlePort.draftRes) && Number(singlePort.draftRes) < 5) {
      errors.push({
        message: `${singlePort.operation} Draft restriction seems too low.`,
        type: "alert",
      });
    }
    if (isLoadingDischarging && Number(singlePort.rate) <= 0) {
      errors.push({
        message: `${singlePort.operation} Load Rate cannot be zero!.`,
        type: "error",
      });
    }
  }

  return errors;
};
